import { __assign, __generator, __rest } from "tslib";
import axios from 'axios';
import throttle from 'lodash/throttle';
import { take, call, put, select, delay } from 'redux-saga/effects';
import { buffers, eventChannel, END } from 'redux-saga';
import { URLS } from '@wix/da-url/dist/src/constants';
import { ConfigSelectors } from '../../config';
import { getCsrfToken } from '../../publicSession/selectors';
import { changeCurrentMediaSource, setCurrentFolderId, uploadFailure, uploadSuccess, uploadProgress, fetchMoreGallections, fetchMoreGallectionsDone, } from '../actions';
import { getCurrentMediaSource, getCurrentFolderId, getGallections, } from '../selectors';
import { getGallectionDeviationStreamId } from '../helpers';
import { withOffset } from '../../Stream';
import { MediaSourceType } from '../types';
var THROTTLE_WAIT = 500;
var DAPI_ROOT = (function () {
    var url = URLS.ROOT + "/dapi/v1";
    if (process.env.NODE_ENV !== 'production') {
        url = url.replace('.com', '.lan');
    }
    return url;
})();
function dapiPost(endpoint, data, options) {
    var onSuccess = options.onSuccess, onError = options.onError, axiosOptions = __rest(options, ["onSuccess", "onError"]);
    return axios
        .request(__assign(__assign({ method: 'post', baseURL: DAPI_ROOT, url: "/" + endpoint }, axiosOptions), { data: data }))
        .then(function (response) {
        onSuccess && onSuccess(response);
        return response.data;
    })
        .catch(function (error) {
        onError && onError(error);
        return false;
    });
}
function createUploadFileChannel(params) {
    return eventChannel(function (emitter) {
        var source = axios.CancelToken.source();
        var throttledEmitter = throttle(emitter, THROTTLE_WAIT);
        dapiPost('submit/upload', params, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            withCredentials: true,
            cancelToken: source.token,
            onUploadProgress: function (e) {
                if (e.lengthComputable) {
                    var progress = e.loaded / e.total;
                    throttledEmitter({ progress: progress });
                }
            },
            onSuccess: function () {
                emitter({ success: true });
            },
            onError: function (error) {
                emitter({ error: new Error('Upload failed') });
                emitter(END);
            },
        });
        return function () {
            source.cancel();
        };
    }, buffers.sliding(2));
}
// Simulate file upload channel events
function createFakeUploadFileChannel(params) {
    return eventChannel(function (emitter) {
        var throttledEmitter = throttle(emitter, THROTTLE_WAIT);
        var progress = 0;
        var interval = setInterval(function () {
            progress = Math.min(1, progress);
            throttledEmitter({ progress: progress });
            if (progress < 1) {
                return (progress += Math.random() / 40);
            }
            emitter({ progress: 1 });
            emitter({ success: true });
            emitter(END);
        }, 50);
        return function () {
            clearInterval(interval);
        };
    });
}
function createReduxChannel(_a) {
    var formData, _b, _c, _d, doFake;
    var file = _a.file, folderid = _a.folderid;
    return __generator(this, function (_e) {
        switch (_e.label) {
            case 0:
                formData = new FormData();
                formData.append('file', file, file.name);
                folderid && formData.append('folderid', folderid);
                // if no folderid is specified, the folder name must be set to "Saved Submissions" as per endpoint spec
                !folderid && formData.append('folder_name', 'Saved Submissions');
                // we only support uploading to stash
                formData.append('action', 'StashPost');
                _c = (_b = formData).append;
                _d = ['_csrf'];
                return [4 /*yield*/, select(getCsrfToken)];
            case 1:
                _c.apply(_b, _d.concat([_e.sent()]));
                if (!(process.env.NODE_ENV !== 'production')) return [3 /*break*/, 3];
                return [4 /*yield*/, select(ConfigSelectors.key('fakeApiRequests'))];
            case 2:
                doFake = _e.sent();
                if (doFake) {
                    return [2 /*return*/, createFakeUploadFileChannel(formData)];
                }
                _e.label = 3;
            case 3: return [2 /*return*/, createUploadFileChannel(formData)];
        }
    });
}
function getOrLoadGallections() {
    var gallections, payload;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, select(getGallections)];
            case 1:
                gallections = _a.sent();
                if (!(!gallections || gallections.length === 0)) return [3 /*break*/, 5];
                // gallections for the currentMediaSource have not yet been loaded, load them
                return [4 /*yield*/, put(fetchMoreGallections())];
            case 2:
                // gallections for the currentMediaSource have not yet been loaded, load them
                _a.sent();
                return [4 /*yield*/, take(function (action) {
                        return action.type === fetchMoreGallectionsDone.toString() &&
                            !!action.payload &&
                            action.payload.type === MediaSourceType.STASH;
                    })];
            case 3:
                payload = (_a.sent()).payload;
                if (!payload || !payload.success) {
                    alert('Sorry could not switch to your sta.sh, please try again.');
                    return [2 /*return*/, []];
                }
                return [4 /*yield*/, select(getGallections)];
            case 4:
                gallections = _a.sent();
                _a.label = 5;
            case 5: return [2 /*return*/, gallections];
        }
    });
}
function reloadStashStream() {
    var currentMediaSource, gallections, currentFolderId, streamId;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, select(getCurrentMediaSource)];
            case 1:
                currentMediaSource = _a.sent();
                if (!(currentMediaSource !== MediaSourceType.STASH)) return [3 /*break*/, 3];
                // change the media source to stash
                return [4 /*yield*/, put(changeCurrentMediaSource(MediaSourceType.STASH))];
            case 2:
                // change the media source to stash
                _a.sent();
                _a.label = 3;
            case 3: return [4 /*yield*/, call(getOrLoadGallections)];
            case 4:
                gallections = _a.sent();
                if (!gallections.length) {
                    // still no folders? everything is terrible, let's crash gracefully
                    return [2 /*return*/];
                }
                currentFolderId = gallections[0].folderId;
                streamId = getGallectionDeviationStreamId(MediaSourceType.STASH, currentFolderId);
                return [4 /*yield*/, put(withOffset.actions.deleteStream(streamId))];
            case 5:
                _a.sent();
                // Load this folder id
                return [4 /*yield*/, put(setCurrentFolderId(gallections[0].folderId))];
            case 6:
                // Load this folder id
                _a.sent();
                return [2 /*return*/];
        }
    });
}
function uploadFileSaga(action) {
    var payload, file, folderId, channel, _a, _b, progress, error, success;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                payload = action.payload;
                file = payload.file;
                return [4 /*yield*/, call(reloadStashStream)];
            case 1:
                _c.sent();
                return [4 /*yield*/, select(getCurrentFolderId)];
            case 2:
                folderId = _c.sent();
                return [4 /*yield*/, call(createReduxChannel, {
                        file: file,
                        folderid: folderId,
                    })];
            case 3:
                channel = _c.sent();
                _c.label = 4;
            case 4:
                if (!true) return [3 /*break*/, 13];
                return [4 /*yield*/, take(channel)];
            case 5:
                _a = _c.sent(), _b = _a.progress, progress = _b === void 0 ? 0 : _b, error = _a.error, success = _a.success;
                if (!error) return [3 /*break*/, 7];
                return [4 /*yield*/, put(uploadFailure(__assign(__assign({}, payload), { folderId: folderId, error: error })))];
            case 6:
                _c.sent();
                return [2 /*return*/];
            case 7:
                if (!success) return [3 /*break*/, 11];
                return [4 /*yield*/, put(uploadSuccess(__assign(__assign({}, payload), { folderId: folderId })))];
            case 8:
                _c.sent();
                return [4 /*yield*/, delay(1000)];
            case 9:
                _c.sent(); // wait arbitrary 1s for stash to update
                return [4 /*yield*/, call(reloadStashStream)];
            case 10:
                _c.sent();
                return [2 /*return*/];
            case 11: return [4 /*yield*/, put(uploadProgress(__assign(__assign({}, payload), { folderId: folderId, progress: progress })))];
            case 12:
                _c.sent();
                return [3 /*break*/, 4];
            case 13: return [2 /*return*/];
        }
    });
}
export default uploadFileSaga;
//# sourceMappingURL=uploadFile.js.map